import type { ReactElement } from 'react'
import { useState } from 'react'
import type { UnvaluableGrant, WarrantGrantView } from '@/api/warrants'
import { formatMinor, formatQuantity } from '~/api/client'
import { chartCss } from '~/charts/theme'
import { WarrantGrantDialog } from '~/components/WarrantGrant'
import { Card, CardHead, StatCard } from '~/components/ui/Card'
import { Button } from '~/components/ui/Controls'
import { DataTable } from '~/components/ui/Table'
import { SETTINGS_PATH } from '~/navigation'
import { regimeLabel, useRemoveWarrantGrant, useWarrants } from '~/state/warrants'

const PAGE_CSS = `
.nw-warrants .nw-stats { display: flex; flex-wrap: wrap; gap: var(--nw-space-6); margin-bottom: var(--nw-stack); }
.nw-warrants .nw-stats > * { flex: 1 1 220px; }
/* Nine columns, and the first of them carries four lines of provenance. They
   do not fit a page, so the table keeps its width and the card scrolls it —
   the alternative is a grant identified one word per line. */
.nw-warrants .nw-data { min-width: 1300px; }
.nw-warrants small { color: var(--nw-muted); }
/* In the grant's own cell rather than in a column of its own: at 1300 a tenth
   column is off the right edge of the card, and the only way to correct a
   grant must not be the one thing on the row nobody scrolls to. */
.nw-warrants .nw-correct { margin-top: var(--nw-space-2); }
.nw-warrants .nw-unvaluable { list-style: none; margin: 0; padding: 0; }
.nw-warrants .nw-unvaluable li { align-items: center; border-top: var(--nw-hairline-width) solid var(--nw-row-rule); display: flex; flex-wrap: wrap; gap: var(--nw-space-3); justify-content: space-between; padding: var(--nw-space-3) 0; }
.nw-warrants .nw-unvaluable .nw-actions { display: flex; align-items: center; gap: var(--nw-space-2); }
`

const GRANT_COLUMNS = [
  { id: 'grant', label: 'Grant', width: '260px' },
  { id: 'vested', label: 'Vested', width: '130px', align: 'right' as const },
  { id: 'strike', label: 'Strike', width: '110px', align: 'right' as const },
  { id: 'price', label: 'Share price', width: '120px', align: 'right' as const },
  { id: 'paper', label: 'Paper value', width: '130px', align: 'right' as const },
  { id: 'cash', label: 'Cash to realise', width: '130px', align: 'right' as const },
  { id: 'left', label: 'Left after', width: '130px', align: 'right' as const },
  { id: 'expiry', label: 'Expiry', width: '120px' },
  { id: 'state', label: 'State', width: '230px' },
]

export function Warrants(): ReactElement {
  const query = useWarrants()
  const [correcting, setCorrecting] = useState<WarrantGrantView | null>(null)
  const data = query.data

  if (!data) {
    return (
      <main className="nw nw-warrants">
        <style>{chartCss + PAGE_CSS}</style>
        <h1>Warrants</h1>
        {query.error ? (
          <p className="nw-flag nw-flag--missing">
            <span className="nw-flag-icon" aria-hidden="true">
              ✕
            </span>
            <span>Could not load the warrants: {query.error.message}</span>
          </p>
        ) : (
          <p className="nw-sub">Loading…</p>
        )}
      </main>
    )
  }

  const { totals, baseCurrency } = data
  const entered = totals.grants + data.unvaluable.length
  const preference = data.grants
    .map((grant) => grant.valuation)
    .find((valuation) => valuation?.preferenceStackMinor != null)
  const valued = totals.grants - totals.unvaluedGrants - totals.expiredGrants
  const nothingValued = totals.grants > 0 && valued === 0
  const taxUnknown = !data.ratesConfigured
  const money = (amountMinor: number | null): string =>
    amountMinor === null ? '—' : formatMinor(amountMinor, baseCurrency)

  return (
    <main className="nw nw-warrants">
      <style>{chartCss + PAGE_CSS}</style>
      <h1>Warrants</h1>
      <p className="nw-sub">
        {`${entered} ${entered === 1 ? 'grant' : 'grants'} · on ${data.date} · all figures in ${baseCurrency}`}
      </p>
      {/* What the page counts and how to read the figures under it. Both were
          in the header, where the first read as the page's description and the
          second sat between the reader and the figures it is about. */}
      {/* What the page does, rather than a claim about whose the grants are.
          A grant is filed against whichever entity the household chose for it,
          company or person, and nothing here checks or shows which — so
          "held personally" was asserting something the product never knew. */}
      <p className="nw-note">
        Each grant is priced off the same round as the shares it converts into.
      </p>
      <p className="nw-note">
        A warrant is worth the spread on what has vested, and getting at that spread costs the
        strike and the tax on it in cash, for shares that cannot then be sold. The three figures
        below only mean anything together.
      </p>
      <p className="nw-note">
        Warrants are not counted in net worth, liquid or runway: they convert into an unsellable
        private share, so treating them as an asset that only adds is exactly the mistake this page
        exists to prevent.
      </p>

      {data.unvaluable.length > 0 && <Unvaluable grants={data.unvaluable} />}

      {data.grants.length === 0 ? (
        data.unvaluable.length === 0 && (
          <p className="nw-empty">
            No warrant grant has been entered yet. Add holding, in the bar above, takes one — its
            strike, its schedule and the company it converts into.
          </p>
        )
      ) : (
        <>
          <div className="nw-stats">
            <StatCard
              label="Vested"
              note={`${formatQuantity(totals.unvestedCount)} unvested, and not counted here`}
            >
              {formatQuantity(totals.vestedCount)}
            </StatCard>
            <StatCard label="Paper value" note="Spread on the vested warrants.">
              {nothingValued ? '—' : money(totals.paperValueMinor)}
            </StatCard>
            <StatCard
              label="Cash to realise"
              note={
                nothingValued
                  ? 'Strike plus tax at exercise.'
                  : `Strike of ${money(totals.strikeCostMinor)} plus tax at exercise.`
              }
            >
              {nothingValued || taxUnknown ? '—' : money(totals.cashRequiredMinor)}
            </StatCard>
            <StatCard label="Left after both" note="In shares that still cannot be sold.">
              {nothingValued || taxUnknown ? '—' : money(totals.netAfterCostMinor)}
            </StatCard>
          </div>

          {preference?.preferenceStackMinor != null && (
            <p className="nw-flag nw-flag--modelled">
              <span className="nw-flag-icon" aria-hidden="true">
                ◆
              </span>
              <span>
                <b>
                  Ranks behind {formatMinor(preference.preferenceStackMinor, baseCurrency)} of
                  preference
                </b>{' '}
                — {data.preferenceCaveat}
              </span>
            </p>
          )}

          {nothingValued && (
            <p className="nw-flag nw-flag--missing">
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>
                <b>Needs input</b> — no price per share has been entered for the underlying, so the warrants
                cannot be valued. A warrant that cannot be valued and a warrant worth nothing are
                different things.
              </span>
            </p>
          )}

          {!nothingValued && totals.unvaluedGrants > 0 && (
            <p className="nw-flag nw-flag--missing">
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>
                {totals.unvaluedGrants} grant{totals.unvaluedGrants === 1 ? '' : 's'} could not be
                priced and are missing from the figures above.
              </span>
            </p>
          )}

          {taxUnknown && (
            <p className="nw-flag nw-flag--missing">
              <span className="nw-flag-icon" aria-hidden="true">
                ✕
              </span>
              <span>
                <b>No exercise tax rate</b> — none is configured for the date shown, so the cash
                the exercise costs is left blank rather than charged at nothing.{' '}
                <a href={SETTINGS_PATH}>Set the rates in Settings</a>, from a date at or before this
                one.
              </span>
            </p>
          )}

          <Card>
            <CardHead title="Every grant" />
            <div className="nw-table-wrap">
              <DataTable dense columns={GRANT_COLUMNS} label="Every warrant grant">
                {data.grants.map((grant) => (
                  <GrantRow
                    key={grant.id}
                    grant={grant}
                    baseCurrency={baseCurrency}
                    onCorrect={() => setCorrecting(grant)}
                  />
                ))}
              </DataTable>
            </div>
          </Card>
        </>
      )}

      <p className="nw-note">{data.disclaimer}</p>

      {correcting && (
        <WarrantGrantDialog editing={correcting} onClose={() => setCorrecting(null)} />
      )}
    </main>
  )
}

/**
 * The grants nothing could value, which the report names rather than adds up.
 *
 * Removal is offered here and not a correction: the dialog restates terms
 * against the figures a grant already has, and these have none. The route now
 * refuses the terms that put a row in this state, so what this is for is the
 * rows written before it did — and the whole point is that the household can
 * reach them, because for as long as one of these took the page down with it
 * the only way out was somebody editing the database.
 */
function Unvaluable({ grants }: { grants: readonly UnvaluableGrant[] }): ReactElement {
  const remove = useRemoveWarrantGrant()
  const [confirming, setConfirming] = useState<number | null>(null)

  return (
    <Card>
      <CardHead title={grants.length === 1 ? 'One grant cannot be valued' : `${grants.length} grants cannot be valued`} />
      <p className="nw-note">
        Entered on terms the valuation refuses, so they are left out of every figure on this page
        rather than counted as nothing. Nothing else here is affected.
      </p>
      {remove.error && <p className="nw-error">{remove.error.message}</p>}
      <ul className="nw-unvaluable">
        {grants.map((grant) => (
          <li key={grant.id}>
            <span>
              <strong>{grant.underlying.symbol}</strong> <small>granted {grant.grantedDate}</small>
              <br />
              <small>{grant.reason}</small>
            </span>
            <span className="nw-actions">
              {confirming === grant.id ? (
                <>
                  <span className="nw-confirm">Remove for good?</span>
                  <Button
                    kind="danger"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(grant.id)}
                  >
                    Remove
                  </Button>
                  <Button onClick={() => setConfirming(null)}>Keep</Button>
                </>
              ) : (
                <Button
                  kind="danger"
                  aria-label={`Remove the ${grant.underlying.symbol} grant of ${grant.grantedDate}`}
                  onClick={() => setConfirming(grant.id)}
                >
                  Remove
                </Button>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function GrantRow({
  grant,
  baseCurrency,
  onCorrect,
}: {
  grant: WarrantGrantView
  baseCurrency: string
  onCorrect: () => void
}): ReactElement {
  const money = (amountMinor: number | null): string =>
    amountMinor === null ? '—' : formatMinor(amountMinor, baseCurrency)

  return (
    <tr>
      <th scope="row">
        {grant.underlying.symbol}
        <br />
        <small>
          {regimeLabel(grant.regime)} · granted {grant.grantedDate}
        </small>
        {grant.valuation && (
          <>
            <br />
            <small>
              {grant.valuation.round ?? 'Unnamed round'}
              {grant.valuation.shareClass !== null && ` (${grant.valuation.shareClass})`} ·{' '}
              {grant.valuation.confidence} confidence · {grant.valuation.ageDays} days old
              {grant.valuation.haircutBp + grant.valuation.extraHaircutBp > 0 &&
                ` · haircut ${(grant.valuation.haircutBp + grant.valuation.extraHaircutBp) / 100} %`}
            </small>
          </>
        )}
        <div className="nw-correct">
          <Button
            aria-label={`Correct the ${grant.underlying.symbol} grant of ${grant.grantedDate}`}
            onClick={onCorrect}
          >
            Correct
          </Button>
        </div>
      </th>
      <td className="nw-num">
        {formatQuantity(grant.vestedCount)}
        <br />
        <small>
          of {formatQuantity(grant.count)}, all by {grant.fullyVestedOn}
        </small>
      </td>
      <td className="nw-num">{formatMinor(grant.strikeMinor, grant.currency, { maximumFractionDigits: 2 })}</td>
      <td className="nw-num">
        {grant.pricePerShareMinor === null
          ? '—'
          : formatMinor(grant.pricePerShareMinor, grant.currency, { maximumFractionDigits: 2 })}
      </td>
      <td className="nw-num">{money(grant.paperValueMinor)}</td>
      <td className="nw-num">{money(grant.cashRequiredMinor)}</td>
      <td className="nw-num">{money(grant.netAfterCostMinor)}</td>
      <td>{grant.expiryDate ?? <small>Expiry not known</small>}</td>
      <td>
        <State grant={grant} baseCurrency={baseCurrency} />
      </td>
    </tr>
  )
}

function State({
  grant,
  baseCurrency,
}: {
  grant: WarrantGrantView
  baseCurrency: string
}): ReactElement {
  if (grant.status === 'expired') {
    return <span>Expired on {grant.expiryDate} — worth nothing since.</span>
  }
  if (grant.status === 'needs_price') {
    return (
      <span>
        {grant.missing === 'fx'
          ? 'No rate to convert the round into the grant currency.'
          : 'No price for the shares yet, so this cannot be valued.'}
      </span>
    )
  }
  if (grant.status === 'not_vested') {
    return (
      <span>
        Nothing vested yet{grant.cliffCleared ? '' : ` — cliff on ${grant.cliffOn}`}.
      </span>
    )
  }
  if (grant.status === 'at_the_money') {
    return <span>At the money — worth nothing yet, at the strike exactly.</span>
  }
  if (grant.status === 'underwater') {
    return <span>Underwater by {formatMinor(grant.underwaterByMinor ?? 0, baseCurrency)}</span>
  }
  return <span>Exercisable</span>
}
